import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Card, CardBody } from '@structural-climbing/ui'
import { useIsMobile } from '../../../lib/useIsMobile'
import { LogoUpload } from '../LogoUpload'
import { CoverUpload } from '../CoverUpload'
import { brandStyle, DEFAULT_PRIMARY, DEFAULT_SECONDARY, isTooPale } from './brandTheme'
import { MemberScreen, type BrandSpot, type MemberScreenKey } from './MemberScreens'

interface Props {
  gymId: string
  name: string
  logoUrl: string | null
  coverUrl: string | null
  primaryColor: string | null
  secondaryColor: string | null
  onLogoChange: (url: string) => void
  onCoverChange: (url: string) => void
  onPrimaryChange: (hex: string) => void
  onSecondaryChange: (hex: string) => void
}

const ALL_SCREENS: MemberScreenKey[] = ['home', 'gyms', 'page']
/** The member screens each setting appears on. */
const SCREENS_FOR: Record<BrandSpot, MemberScreenKey[]> = {
  logo: ['home', 'gyms', 'page'],
  cover: ['gyms', 'page'],
  primary: ['home', 'gyms', 'page'],
  secondary: ['home', 'page'],
}

/**
 * Settings → Branding. Every setting says where members see it. On a wide
 * screen the three member screens sit beside the form and light up where the
 * setting under the pointer appears; on a phone each setting opens its own
 * screens instead, since three phones do not fit beside a form.
 */
export function BrandingSettings(props: Props) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [pointed, setPointed] = useState<BrandSpot | null>(null)
  const [open, setOpen] = useState<BrandSpot | null>(null)
  const brand = { name: props.name, logoUrl: props.logoUrl, coverUrl: props.coverUrl }
  const primary = props.primaryColor ?? DEFAULT_PRIMARY
  const secondary = props.secondaryColor ?? DEFAULT_SECONDARY

  const screensRow = (screens: MemberScreenKey[], highlight: BrandSpot | null) => (
    <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
      {screens.map((s) => (
        <figure key={s} className="m-0 flex snap-start flex-col items-center gap-1.5">
          <MemberScreen screen={s} brand={brand} highlight={highlight} />
          <figcaption className="text-xs text-foreground/55">
            {t(`settings.branding.screen.${s}`)}
          </figcaption>
        </figure>
      ))}
    </div>
  )

  const setting = (spot: BrandSpot, title: string, body: ReactNode) => (
    <Card
      shadow="sm"
      onMouseEnter={isMobile ? undefined : () => setPointed(spot)}
      onMouseLeave={isMobile ? undefined : () => setPointed(null)}
      className={pointed === spot ? 'ring-2 ring-amber-300' : ''}
    >
      <CardBody className="flex flex-col gap-3 px-4 py-4">
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-xs text-foreground/55">
            {t('settings.branding.showsOn', { places: t(`settings.branding.where.${spot}`) })}
          </p>
        </div>
        {body}
        {isMobile && (
          <>
            <Button
              size="sm"
              variant={open === spot ? 'solid' : 'flat'}
              color={open === spot ? 'primary' : 'default'}
              className="self-start"
              aria-expanded={open === spot}
              onPress={() => setOpen(open === spot ? null : spot)}
            >
              {open === spot ? t('settings.branding.hideWhere') : t('settings.branding.seeWhere')}
            </Button>
            {open === spot && screensRow(SCREENS_FOR[spot], spot)}
          </>
        )}
      </CardBody>
    </Card>
  )

  const colourField = (value: string, onChange: (hex: string) => void, label: string) => (
    <div className="flex items-center gap-3">
      <input
        type="color"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-16 cursor-pointer rounded border border-divider bg-transparent"
      />
      <span className="font-mono text-sm text-foreground/60">{value}</span>
    </div>
  )

  const fields = (
    <div className="flex min-w-0 flex-col gap-4">
      {setting(
        'logo',
        t('settings.sectionLogo'),
        <LogoUpload
          gymId={props.gymId}
          imageUrl={props.logoUrl}
          onLogoChange={props.onLogoChange}
        />,
      )}
      {setting(
        'cover',
        t('settings.coverPhoto'),
        <CoverUpload
          gymId={props.gymId}
          coverUrl={props.coverUrl}
          onCoverChange={props.onCoverChange}
        />,
      )}
      {setting(
        'primary',
        t('settings.primaryColor'),
        <>
          {colourField(primary, props.onPrimaryChange, t('settings.primaryColor'))}
          {isTooPale(primary) && (
            <Alert color="warning" variant="flat" description={t('settings.branding.tooPale')} />
          )}
        </>,
      )}
      {setting(
        'secondary',
        t('settings.secondaryColor'),
        colourField(secondary, props.onSecondaryChange, t('settings.secondaryColor')),
      )}
    </div>
  )

  return (
    <div style={brandStyle(props.primaryColor, props.secondaryColor)}>
      {isMobile ? (
        <div className="flex flex-col gap-4">
          <Card shadow="sm">
            <CardBody className="flex flex-col gap-2 px-4 py-4">
              <p className="text-sm font-semibold">{t('settings.branding.standsOut')}</p>
              {screensRow(ALL_SCREENS, null)}
              <p className="text-xs text-foreground/50">{t('settings.branding.swipeHint')}</p>
            </CardBody>
          </Card>
          {fields}
        </div>
      ) : (
        <div className="grid items-start gap-6 min-[1400px]:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex flex-col gap-2 min-[1400px]:order-2 min-[1400px]:sticky min-[1400px]:top-4">
            <p className="text-sm font-semibold">{t('settings.branding.standsOut')}</p>
            {screensRow(ALL_SCREENS, pointed)}
            <p className="text-xs text-foreground/50">{t('settings.branding.hoverHint')}</p>
          </div>
          {fields}
        </div>
      )}
    </div>
  )
}
